import React, { useState, useMemo, useEffect } from "react";
import { API } from "aws-amplify";
import { gql, useQuery, useMutation, useApolloClient } from "@apollo/client";
import {
  Stack,
  TextField,
  Tooltip,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  Typography,
} from "@mui/material";
import groupVAT from "../lib/groupVAT";
import { VATMaxAmountPerInv, VATMaxLines } from "../settings/formFieldEnum";
import SimpleTable from "./SimpleTable";
import { LoadingButton } from "@mui/lab";
// import { onCreate } from "../function/onCreate";
import onCreateInvoices from "../modules/entityModules/billing/onCreate";
import { toChinaDateString } from "../lib/chinaDate";


const getPO = /* GraphQL */ `
  query GetPO($id: ID!) {
    getPO(id: $id) {
      id
      number
      itemNumber
      date

      materialID

      materialEntityRelationID
      vendor {
        id
        name
      }
      customer {
        id
        name
        billingInfo {
          billingName
          regNumber
          phoneNumber
          taxID
          address
          bank
          accountNumber
          swiftCode
          bankAddress
          routingNumber
          iban
          entityUserRelationID
        }
      }

      qty
      openQty
      price
      priceCur
      paymentTerms
      incoTerms {
        terms
        condition
        entityUserRelationID
        __typename
      }
      billingEntityID
      CRD
      shippingStatus
      billingStatus
      paymentStatus
      importAgentID
      paymentAgentID
      forwarderID

      version
      latestVersion
      statusCode

      invoice {
        nextToken
        __typename
      }

      __typename
    }
  }
`;

const getEntityUserRelation = /* GraphQL */ `
  query GetEntityUserRelation($id: ID!) {
    getEntityUserRelation(id: $id) {
      id
      role
      title
      shownFn
      shownLn
      shownMn
      statusCode
      isEmailVerified
      userID
      user {
        id
        sub
        email
        statusCode
        fn
        mn
        ln
        phoneNumber
        address
        groupToRead
        groupToEdit
        groupToDelete
        owner
        definerID
        isDeleted
        createdAt
        updatedAt
        __typename
      }
      entityID
      entity {
        id
        isVerified
        name
        alias
        type
        businessType
        statusCode
        paymentTerms
        groupToRead
        groupToEdit
        groupToDelete
        definerID
        isDeleted
        createdAt
        updatedAt
        __typename
      }

      __typename
    }
  }
`;

const createInvoice = /* GraphQL */ `
  mutation CreateInvoice(
    $input: CreateInvoiceInput!
    $condition: ModelInvoiceConditionInput
  ) {
    createInvoice(input: $input, condition: $condition) {
      id
      category
      type
      number
      itemNumber
      date
      price
      qty
      currency
      note
      desc
      poID

      paymentDueDate
      billingEntityID

      shippingID

      definerID
      paymentStatus
      deductionDate
      createdAt
      updatedAt
      __typename
    }
  }
`;

export default function InvoiceGen({
  onClose,
  selectionModel,
  setSelectionModel,
  definerID,
}) {
  const client = useApolloClient();

  const [items, setItems] = useState();
  // const [invoiceType, set] = useState();

  useEffect(() => {
    async function func() {
      const arr = await Promise.all(
        selectionModel.map(async (x) => {
          const { data } = await client.query({
            query: gql(getPO),
            variables: { id: x.poID },
          });

          const PO = data?.getPO;
          const {
            paymentTerms,
            price,
            priceCur,
            number,
            itemNumber,
            vendor,
            customer,
          } = PO;
          return {
            shippingID: x.id,
            materialID: PO.materialID,
            materialEntityRelationID: PO.materialEntityRelationID,
            code: x.code,
            mfr: x.mfr,
            mpn: x.mpn,
            poID: x.poID,
            unit: x.material?.unit,
            desc: x.material?.desc || x.mpn,
            poNumber: number,
            poItemNumber: itemNumber,
            qty: x.qty,
            price,
            priceCur,
            paymentTerms,
            shippingDate: x.date,
            vendorName: vendor.name,
            customerID: customer.id,
            customerName: customer.name,
            billingInfo: customer.billingInfo,
          };
        })
      );
      setItems(arr);
    }

    if (selectionModel?.length) {
      func();
    }
  }, [selectionModel, client]);

  if (!items?.length) return null;

  if ([...new Set(items?.map((x) => x.customerID))].length > 1) {
    alert("Please select POs with the same customer");
    return;
  }

  if (items?.every((x) => x.priceCur === "CNY"))
    return (
      <VATGen
        items={items}
        definerID={definerID}
        setSelectionModel={setSelectionModel}
        onClose={onClose}
      />
    );
  else if (items?.every((x) => x.priceCur === "USD"))
    return (
      <PIGen
        items={items}
        definerID={definerID}
        setSelectionModel={setSelectionModel}
        onClose={onClose}
      />
    );
  else {
    console.log({ items });
    alert("Please select POs with the same currency");
    return null;
  }
}

function VATGen({ items, definerID, setSelectionModel, onClose }) {
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);

  const headers = [
    "invoiceNumber",
    "invoiceItemNumber",
    "mfr",
    "mpn",
    "code",
    "qty",
    "price",
    "priceCur",
    "amount",
    "subTotal",
  ];
  const [startingVAT, setStartingVAT] = useState(1);

  // Original items with invoice numbers assigned (for database records)
  const vats = useMemo(() => {
    const aggregatedItems = groupVAT(
      items,
      VATMaxAmountPerInv,
      VATMaxLines,
      startingVAT
    )?.flat();
    if (!aggregatedItems?.length) return [];
    return aggregatedItems.map((x) => {
      return {
        ...x,
        amount: (x.qty * x.price).toFixed(3) * 1,
        subTotal:
          x.invoiceItemNumber === 1
            ? aggregatedItems
                .filter((y) => y.invoiceNumber === x.invoiceNumber)
                .reduce((a, b) => a + b.qty * b.price, 0)
                .toFixed(3) * 1
            : null,
      };
    });
  }, [items, startingVAT]);

  // Display items - merge duplicates BEFORE grouping so they combine properly
  const displayVats = useMemo(() => {
    if (!items?.length) return [];

    // First merge duplicate items by code, mpn, and price
    const mergedMap = new Map();
    for (const item of items) {
      const key = `${item.code || ""}_${item.mpn || ""}_${item.price}`;
      if (mergedMap.has(key)) {
        const existing = mergedMap.get(key);
        // Ensure qty is treated as number
        existing.qty = Number(existing.qty) + Number(item.qty);
      } else {
        // Create a copy with qty as number and reset 'added' flag from previous groupVAT calls
        mergedMap.set(key, {
          ...item,
          qty: Number(item.qty),
          added: undefined,
        });
      }
    }
    const mergedItems = Array.from(mergedMap.values());

    // Now group merged items into invoices
    const aggregatedItems = groupVAT(
      mergedItems,
      VATMaxAmountPerInv,
      VATMaxLines,
      startingVAT
    )?.flat();

    if (!aggregatedItems?.length) return [];

    return aggregatedItems.map((x) => {
      return {
        ...x,
        amount: (x.qty * x.price).toFixed(3) * 1,
        subTotal:
          x.invoiceItemNumber === 1
            ? aggregatedItems
                .filter((y) => y.invoiceNumber === x.invoiceNumber)
                .reduce((a, b) => a + b.qty * b.price, 0)
                .toFixed(3) * 1
            : null,
      };
    });
  }, [items, startingVAT]);

  const totaoValue =
    displayVats.reduce((a, v) => a + v.price * v.qty, 0).toFixed(3) * 1;
  const totalQty = displayVats.reduce((a, v) => a + v.qty, 0).toFixed(3) * 1;

  // console.log({ vats });

  // Use items[0] for billing info since vats might be empty during computation
  const { vendorName, customerName, billingInfo } = items[0] || {};
  const {
    accountNumber,
    bank,
    bankAddress,
    billingName,
    iban,
    routingNumber,
    swiftCode,
    entityUserRelationID,
    taxID,
  } = billingInfo || {};

  const { data: getEntityUserRelationData } = useQuery(
    gql(getEntityUserRelation),
    { variables: { id: entityUserRelationID }, skip: !entityUserRelationID }
  );

  const { user } = getEntityUserRelationData?.getEntityUserRelation || {};
  const { address, phoneNumber, email } = user || {};

  const onSubmit = async () => {
    setLoading(true);
    const date = toChinaDateString();
    const batch = Date.now().toString().slice(1, 9);

    // Helper function to create a normalized key for matching
    const createKey = (item) => {
      const code = String(item.code || "").trim();
      const mpn = String(item.mpn || "").trim();
      const price = Number(item.price) || 0;
      return `${code}_${mpn}_${price}`;
    };

    // Build a map from code+mpn+price to merged invoice number
    const mergedInvoiceMap = new Map();
    let fallbackInvoiceNumber = "";
    for (const item of displayVats) {
      const key = createKey(item);
      mergedInvoiceMap.set(key, {
        invoiceNumber: item.invoiceNumber,
        invoiceItemNumber: item.invoiceItemNumber,
      });
      // Store first invoice number as fallback (all merged items should have same invoice number)
      if (!fallbackInvoiceNumber && item.invoiceNumber) {
        fallbackInvoiceNumber = item.invoiceNumber;
      }
    }

    // Create invoice items from original items (all 17 items), but use merged invoice numbers
    // This keeps individual shippingID records for 分配表, but groups them under one invoice
    const invoiceItemsWithMergedNumbers = items.map((x) => {
      let paymentDueDate;
      let days = 130;

      if (x.shippingDate) {
        const paymentDue =
          Math.max(new Date(x.shippingDate).getTime(), Date.now()) +
          days * 86400000;

        paymentDueDate = toChinaDateString(paymentDue);
      }

      // Look up the merged invoice number for this item using normalized key
      const key = createKey(x);
      const merged = mergedInvoiceMap.get(key);
      const invoiceNumber = merged?.invoiceNumber || fallbackInvoiceNumber;

      return {
        number: invoiceNumber,
        shippingID: x.shippingID,
        poID: x.poID,
        qty: x.qty,
        currency: x.priceCur,
        price: x.price,
        desc: x.desc,
        paymentDueDate,
      };
    });

    // Ensure all items have invoice numbers (should be guaranteed by fallback, but check anyway)
    const validItems = invoiceItemsWithMergedNumbers.filter(
      (item) => item.number
    );

    // Group items by invoice number and assign sequential item numbers within each invoice
    const invoiceGroups = new Map();
    for (const item of validItems) {
      if (!invoiceGroups.has(item.number)) {
        invoiceGroups.set(item.number, []);
      }
      invoiceGroups.get(item.number).push(item);
    }

    // Flatten and assign item numbers sequentially within each invoice
    const invoiceItems = [];
    for (const [invoiceNumber, itemsInGroup] of invoiceGroups) {
      itemsInGroup.forEach((item, idx) => {
        invoiceItems.push({
          ...item,
          itemNumber: idx + 1, // Sequential item number within each invoice
        });
      });
    }

    const values = {
      date,
      batch,
      billingEntityID: items[0].customerID,
      category: "VAT",
      definerID,
      invoiceItems,
    };

    await onCreateInvoices({ client, values, type: "OUTGOING" });

    await client.refetchQueries({ include: ["InvoiceByType"] });

    const res = await API.post("iSupplyAPI", "/restricted", {
      body: {
        operation: "SEND_VAT_BATCH_NUMBER",
        data: {
          batch,
          to: "ying.liu@hankeno.com",
          cc: "steven.lu@hankeno.com",
        },
      },
    });

    setSelectionModel && setSelectionModel([]);
    setLoading(false);

    onClose && onClose();
  };

  return (
    <Stack spacing={3} minWidth={1600} p={5}>
      <Stack direction="row" spacing={4}>
        <TextField
          label="STARTING VAT NUMBER"
          sx={{ alignSelf: "flex-start", width: 300 }}
          value={startingVAT}
          placeholder="STARTING VAT NUMBER"
          onChange={(e) => setStartingVAT(e.target.value)}
        />

        <Stack>
          <Typography variant="h5">Total Value: {totaoValue}</Typography>
          <Typography variant="h5">Total Qty: {totalQty}</Typography>
        </Stack>

        <Stack>
          <Stack direction="row">
            <Typography variant="title" width={120}>
              VENDOR:
            </Typography>
            <Typography variant="title">{vendorName}</Typography>
          </Stack>
          <Stack direction="row">
            <Typography variant="title" width={120}>
              CUSTOMER:
            </Typography>
            <Typography variant="title">{customerName}</Typography>
          </Stack>
          <Stack direction="row">
            <Typography variant="title" width={120}>
              BILL TO:
            </Typography>
            <Stack>
              <Typography variant="title">{billingName}</Typography>
              <Typography variant="title">TaxID: {taxID}</Typography>
              <Typography variant="title">
                Billing Address: {address} {phoneNumber} {email}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      <SimpleTable headers={headers} rows={displayVats} />
      <LoadingButton
        loading={loading}
        sx={{ alignSelf: "center" }}
        variant="contained"
        onClick={onSubmit}
      >
        GENERATE INVOICES
      </LoadingButton>
    </Stack>
  );
}

function PIGen({ items, definerID, setSelectionModel, onClose }) {
  const [loading, setLoading] = useState(false);
  const client = useApolloClient();

  const date = toChinaDateString();
  const number = `${Date.now().toString().slice(1, 9)}`;

  const invoiceItems = items.map((x, i) => {
    let paymentDueDate;
    let days = 0;
    if (x?.paymentTerms.startsWith("NET")) {
      days = x.paymentTerms.split(" ")[1] * 1;
    }

    if (x.shippingDate) {
      const paymentDue =
        Math.max(new Date(x.shippingDate).getTime(), Date.now()) +
        days * 86400000;

      paymentDueDate = toChinaDateString(paymentDue);
    }

    return {
      ...x,
      number,
      itemNumber: i + 1,
      shippingID: x.shippingID,
      poID: x.poID,
      qty: x.qty,
      currency: x.priceCur,
      price: x.price,
      desc: x.desc,
      paymentDueDate,
      amount: (x.qty * x.price).toFixed(3) * 1,
    };
  });

  const values = {
    date,
    billingEntityID: items[0].customerID,
    category: "PI",
    definerID,
    invoiceItems,
  };

  const onSubmit = async () => {
    setLoading(true);
    // debugger;

    await onCreateInvoices({ client, values, type: "OUTGOING" });

    await client.refetchQueries({
      include: ["InvoiceByType", "ShippingByTypeAndMaterial"],
    });

    setSelectionModel && setSelectionModel([]);
    setLoading(false);

    onClose && onClose();
  };

  const headers = [
    "number",
    "itemNumber",
    "mfr",
    "mpn",
    "code",
    "poNumber",
    "poItemNumber",
    "qty",
    "price",
    "currency",
    "amount",
  ];

  return (
    <Stack minWidth={1200} spacing={5}>
      <SimpleTable headers={headers} rows={invoiceItems} />
      <LoadingButton
        loading={loading}
        sx={{ alignSelf: "center" }}
        variant="contained"
        onClick={onSubmit}
      >
        GENERATE INVOICES
      </LoadingButton>
    </Stack>
  );
}
